/**
 * Types for BudgetManager component
 */

import type {
  BudgetSettings,
  VenueBudgetComputed,
  ProposalBudgetFull,
  VenueRoomBreakdown,
  MiscellaneousCostItem,
  ExchangeRates,
} from '@/services/BudgetService';

export type { BudgetSettings, VenueBudgetComputed, ProposalBudgetFull, VenueRoomBreakdown, MiscellaneousCostItem, ExchangeRates };

/**
 * Supported currencies for display
 */
export const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF'] as const;
export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

/**
 * Currency display info
 */
export const CURRENCY_INFO: Record<SupportedCurrency, { symbol: string; name: string }> = {
  USD: { symbol: '$', name: 'US Dollar' },
  EUR: { symbol: '€', name: 'Euro' },
  GBP: { symbol: '£', name: 'British Pound' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar' },
  AUD: { symbol: 'A$', name: 'Australian Dollar' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc' },
};

/**
 * Tab types for the budget manager
 */
export type BudgetTab = 'summary' | string; // 'summary' or venue_id

/**
 * Props for BudgetManager component
 */
export interface BudgetManagerProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  onSave?: () => void;
}

/**
 * Props for BudgetSummary component
 */
export interface BudgetSummaryProps {
  budget: ProposalBudgetFull;
  /** Current settings from form state (may differ from budget.settings) */
  currentSettings: BudgetSettings;
  /** Venue form data for dynamic calculations */
  venueFormData: Record<string, VenueFormData>;
  /** Event's base currency for display */
  eventCurrency: string;
  /** Exchange rates for currency conversion */
  exchangeRates: ExchangeRates | null;
  onSelectVenue: (venueId: string) => void;
}

/**
 * Props for VenueBudgetTab component (controlled - no internal fetching)
 */
export interface VenueBudgetTabProps {
  /** Server data for display (non-editable computed fields) */
  venueBudget: VenueBudgetComputed;
  /** Editable form data from parent */
  venueFormData: VenueFormData;
  attendees: number;
  nights: number;
  settings: BudgetSettings;
  /** Event's base currency for display in Trip Breakdown */
  eventCurrency: string;
  /** Exchange rates for currency conversion */
  exchangeRates: ExchangeRates | null;
  /** Callback when room breakdown values change */
  onRoomBreakdownChange: (data: Partial<VenueRoomBreakdown>) => void;
  /** Callback when activities breakdown values change */
  onActivitiesBreakdownChange: (data: Partial<VenueActivitiesBreakdown>) => void;
  /** Callback when meals breakdown values change */
  onMealsBreakdownChange: (data: Partial<VenueMealsBreakdown>) => void;
  /** Callback when meetings breakdown values change */
  onMeetingsBreakdownChange: (data: Partial<VenueMeetingsBreakdown>) => void;
  /** Callback when venue currency changes */
  onVenueCurrencyChange: (currency: string) => void;
  /** Callback when other costs (flight, ground transport, misc) change */
  onOtherCostsChange: (data: Partial<VenueOtherCostsFormData>) => void;
  /** Callback when budget note changes */
  onBudgetNoteChange: (note: string | null) => void;
  /** Other venues available as copy targets */
  otherVenues?: CopyTargetVenue[];
  /** Callback to copy budget sections to target venues */
  onCopyBudget?: (targetShortlistIds: string[], sections: CopyBudgetSections) => void;
}

/**
 * Props for RoomBreakdown component
 */
export interface RoomBreakdownProps {
  shortlistId: string;
  breakdown: VenueRoomBreakdown;
  nights: number;
  displayCurrency: string;
  onChange: (data: Partial<VenueRoomBreakdown>) => void;
}

/**
 * Props for SettingsPanel component
 */
export interface SettingsPanelProps {
  settings: BudgetSettings;
  /** Event's base currency (source of truth) — used for symbol prefixes on fixed-value inputs */
  eventCurrency: string;
  onChange: (settings: Partial<BudgetSettings>) => void;
}

/**
 * Props for CurrencyToggle component
 */
export interface CurrencyToggleProps {
  value: string;
  onChange: (currency: string) => void;
}

/**
 * Cost values for a single category (per-attendee and subtotal)
 */
export interface CategoryCostValues {
  per_attendee: number | null;
  subtotal: number | null;
}

/**
 * All destination cost values for a venue
 */
export interface DestinationCostValues {
  flight_cost: CategoryCostValues;
  activities: CategoryCostValues;
  ground_transport: CategoryCostValues;
  meals: CategoryCostValues;
  meetings: CategoryCostValues;
  miscellaneous: CategoryCostValues;
}

/**
 * Activities breakdown for a venue
 */
export interface VenueActivitiesBreakdown {
  activities_number: number | null;
  activities_cost_per: number | null; // cents per activity
  activities_currency: string;
}

/**
 * Meals breakdown for a venue
 */
export interface VenueMealsBreakdown {
  breakfast_cost_per_attendee: number | null; // cents per breakfast per attendee
  breakfast_count: number;
  lunch_cost_per_attendee: number | null; // cents per lunch per attendee
  lunch_count: number;
  dinner_cost_per_attendee: number | null; // cents per dinner per attendee
  dinner_count: number;
  alcohol_cost_per_attendee: number | null; // cents per alcohol per attendee
  alcohol_count: number;
  meals_currency: string;
}

/**
 * Meetings breakdown for a venue
 */
export interface VenueMeetingsBreakdown {
  meeting_room_rental: number | null; // cents per meeting
  meeting_setup_fees: number | null; // cents per meeting
  meeting_av_cost: number | null; // cents per meeting
  number_of_meetings: number;
  coffee_cost_per_attendee: number | null; // cents per coffee per attendee
  coffee_count: number;
  meetings_currency: string;
}

/**
 * Venue-level other costs form data
 */
export interface VenueOtherCostsFormData {
  flight_cost_per_attendee: number | null; // cents
  ground_transport_cost: number | null; // cents
  miscellaneous_costs: MiscellaneousCostItem[] | null;
  budget_note: string | null;
}

/**
 * Per-venue form data (all editable data for one venue)
 */
export interface VenueFormData {
  shortlistId: string;
  destinationKey: string; // "city:country"
  /** Venue's input currency (all costs below stored in this currency) */
  venueCurrency: string;
  roomBreakdown: VenueRoomBreakdown;
  activitiesBreakdown: VenueActivitiesBreakdown;
  mealsBreakdown: VenueMealsBreakdown;
  meetingsBreakdown: VenueMeetingsBreakdown;
  /** Venue-level other costs (flight, ground transport, miscellaneous) */
  otherCosts: VenueOtherCostsFormData;
  /** DEPRECATED: Use otherCosts instead - kept for backward compatibility during migration */
  destinationCosts?: DestinationCostValues;
  /** DEPRECATED: Cached destination_content.id - no longer used */
  destinationContentId?: string;
}

/**
 * Tracks which fields have been modified
 */
export interface DirtyFields {
  settings: boolean;
  venues: Set<string>; // shortlist IDs with any changes
}

/**
 * Central form state for the budget manager
 */
export interface BudgetFormState {
  settings: BudgetSettings;
  venues: Record<string, VenueFormData>; // keyed by shortlist_id
  isDirty: boolean;
  dirtyFields: DirtyFields;
}

/**
 * Sections that can be copied between venue budgets
 */
export interface CopyBudgetSections {
  meals: boolean;
  meetings: boolean;
  activities: boolean;
  otherCosts: boolean; // flights, ground transport, misc items
  roomBreakdown: boolean;
  budgetNote: boolean;
}

/**
 * Info about a venue available as a copy target
 */
export interface CopyTargetVenue {
  shortlistId: string;
  venueName: string;
  venueCurrency: string;
}
